import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Crown, Shield, Trophy, Coins, Ban, Plus, RefreshCw, Trash2,
  Lock, User, Upload, Edit, ToggleLeft, ToggleRight,
  Link as LinkIcon, Image, Camera, ChevronDown, Code, Search, X, Check,
} from 'lucide-react';

// ---- Add Phrase Dialog ----
export const AddPhraseDialog = ({ isItalian, open, onOpenChange, newPhrase, setNewPhrase, handleAddPhrase }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl uppercase tracking-wider">
          {isItalian ? 'Nuova Frase Bot' : 'New Bot Phrase'}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">Comando</Label>
          <Input value={newPhrase.command} onChange={(e) => setNewPhrase({ ...newPhrase, command: e.target.value })} placeholder="!ciao" className="bg-[#050508] border-white/10 text-white" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Frase' : 'Phrase'}</Label>
          <Input value={newPhrase.phrase} onChange={(e) => setNewPhrase({ ...newPhrase, phrase: e.target.value })} placeholder={isItalian ? 'Ciao {gamertag}! Benvenuto!' : 'Hello {gamertag}! Welcome!'} className="bg-[#050508] border-white/10 text-white" />
          <p className="text-xs text-slate-500">{isItalian ? 'Usa {gamertag} per inserire il nome del giocatore' : 'Use {gamertag} to insert player name'}</p>
        </div>
        <Button onClick={handleAddPhrase} className="w-full bg-[#00E676] hover:bg-[#00E676]/90 text-black font-bold uppercase tracking-wider">
          {isItalian ? 'Aggiungi Frase' : 'Add Phrase'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// ---- Banner Dialog ----
export const BannerDialog = ({ isItalian, open, onOpenChange, editingBanner, bannerForm, setBannerForm, handleSaveBanner }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl uppercase tracking-wider">
          {editingBanner ? (isItalian ? 'Modifica Banner' : 'Edit Banner') : (isItalian ? 'Nuovo Banner' : 'New Banner')}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Titolo' : 'Title'} *</Label>
          <Input value={bannerForm.title} onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })} placeholder={isItalian ? 'Titolo del banner' : 'Banner title'} className="bg-[#050508] border-white/10 text-white" data-testid="banner-title-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Messaggio' : 'Message'} *</Label>
          <textarea value={bannerForm.message} onChange={(e) => setBannerForm({ ...bannerForm, message: e.target.value })} placeholder={isItalian ? 'Messaggio del banner (testo semplice)' : 'Banner message (plain text)'} className="w-full px-3 py-2 bg-[#050508] border border-white/10 text-white min-h-[60px] resize-none text-sm" data-testid="banner-message-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#00F0FF] uppercase tracking-wider text-xs font-bold flex items-center gap-2">
            <Code size={12} />
            {isItalian ? 'Contenuto HTML (opzionale)' : 'HTML Content (optional)'}
          </Label>
          <p className="text-xs text-slate-500">{isItalian ? 'Se compilato, il banner mostrerà questo HTML al posto del testo semplice' : 'If filled, the banner will display this HTML instead of plain text'}</p>
          <textarea
            value={bannerForm.html_content}
            onChange={(e) => setBannerForm({ ...bannerForm, html_content: e.target.value })}
            placeholder={isItalian ? 'Incolla il tuo codice HTML qui...' : 'Paste your HTML code here...'}
            className="w-full px-3 py-2 bg-[#050508] border border-[#00F0FF]/20 text-[#00F0FF]/80 font-mono text-xs min-h-[120px] resize-y focus:border-[#00F0FF]/50 focus:outline-none"
            data-testid="banner-html-input"
            spellCheck={false}
          />
          {bannerForm.html_content && (
            <div className="space-y-1">
              <Label className="text-slate-400 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Anteprima HTML' : 'HTML Preview'}</Label>
              <div
                className="p-4 bg-[#050508] border border-white/10 rounded overflow-hidden"
                dangerouslySetInnerHTML={{ __html: bannerForm.html_content }}
                data-testid="banner-html-preview"
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Tipo' : 'Type'}</Label>
            <Select value={bannerForm.banner_type} onValueChange={(value) => setBannerForm({ ...bannerForm, banner_type: value })}>
              <SelectTrigger className="bg-[#050508] border-white/10 text-white" data-testid="banner-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0d15] border-white/10">
                <SelectItem value="info" className="text-[#00F0FF]">Info</SelectItem>
                <SelectItem value="warning" className="text-[#FFEA00]">{isItalian ? 'Avviso' : 'Warning'}</SelectItem>
                <SelectItem value="urgent" className="text-[#FF1744]">{isItalian ? 'Urgente' : 'Urgent'}</SelectItem>
                <SelectItem value="success" className="text-[#00E676]">{isItalian ? 'Successo' : 'Success'}</SelectItem>
                <SelectItem value="promo" className="text-purple-400">Promo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Priorità' : 'Priority'}</Label>
            <Input type="number" min="0" max="100" value={bannerForm.priority} onChange={(e) => setBannerForm({ ...bannerForm, priority: parseInt(e.target.value) || 0 })} className="bg-[#050508] border-white/10 text-white" data-testid="banner-priority-input" />
            <p className="text-xs text-slate-500">{isItalian ? 'Più alto = più importante' : 'Higher = more important'}</p>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold flex items-center gap-2"><LinkIcon size={12} />Link ({isItalian ? 'opzionale' : 'optional'})</Label>
          <Input value={bannerForm.link} onChange={(e) => setBannerForm({ ...bannerForm, link: e.target.value })} placeholder="https://..." className="bg-[#050508] border-white/10 text-white" data-testid="banner-link-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold flex items-center gap-2"><Image size={12} />{isItalian ? 'URL Immagine' : 'Image URL'} ({isItalian ? 'opzionale' : 'optional'})</Label>
          <Input value={bannerForm.image_url} onChange={(e) => setBannerForm({ ...bannerForm, image_url: e.target.value })} placeholder="https://..." className="bg-[#050508] border-white/10 text-white" data-testid="banner-image-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Data Inizio' : 'Start Date'}</Label>
            <Input type="datetime-local" value={bannerForm.start_date} onChange={(e) => setBannerForm({ ...bannerForm, start_date: e.target.value })} className="bg-[#050508] border-white/10 text-white" data-testid="banner-start-date" />
            <p className="text-xs text-slate-500">{isItalian ? 'Vuoto = subito attivo' : 'Empty = immediately active'}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Data Fine' : 'End Date'}</Label>
            <Input type="datetime-local" value={bannerForm.end_date} onChange={(e) => setBannerForm({ ...bannerForm, end_date: e.target.value })} className="bg-[#050508] border-white/10 text-white" data-testid="banner-end-date" />
            <p className="text-xs text-slate-500">{isItalian ? 'Vuoto = nessuna scadenza' : 'Empty = no expiration'}</p>
          </div>
        </div>
        <div className="flex items-center justify-between p-3 bg-[#050508] border border-white/10">
          <span className="text-sm text-slate-300">{isItalian ? 'Banner Attivo' : 'Banner Active'}</span>
          <button type="button" onClick={() => setBannerForm({ ...bannerForm, is_active: !bannerForm.is_active })} className={`p-1 rounded ${bannerForm.is_active ? 'text-[#00E676]' : 'text-slate-500'}`} data-testid="banner-active-toggle">
            {bannerForm.is_active ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
          </button>
        </div>
        <Button onClick={handleSaveBanner} className="w-full bg-[#00E676] hover:bg-[#00E676]/90 text-black font-bold uppercase tracking-wider" data-testid="save-banner-btn">
          {editingBanner ? (isItalian ? 'Salva Modifiche' : 'Save Changes') : (isItalian ? 'Crea Banner' : 'Create Banner')}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// ---- Coins Dialog ----
export const CoinsDialog = ({ isItalian, open, onOpenChange, selectedUser, clubs, coinsTarget, setCoinsTarget, coinsAction, setCoinsAction, coinsAmount, setCoinsAmount, handleModifyCoins }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
      <DialogHeader><DialogTitle className="font-heading text-xl uppercase tracking-wider">{isItalian ? 'Modifica Coins' : 'Modify Coins'}</DialogTitle></DialogHeader>
      {selectedUser && (
        <div className="space-y-4 mt-4">
          <div className="p-4 bg-[#050508] border border-white/10">
            <p className="font-medium text-white">{selectedUser.name}</p>
            {selectedUser.gamertag && <p className="text-sm text-[#00F0FF]">{selectedUser.gamertag}</p>}
            <div className="flex gap-4 mt-2 text-xs text-slate-400">
              <span>Personali: {(selectedUser.personal_coins || 0).toLocaleString()}</span>
              {clubs.find(c => c.captain_id === selectedUser.user_id) && <span>Club: {clubs.find(c => c.captain_id === selectedUser.user_id)?.coins?.toLocaleString() || 0}</span>}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Destinazione' : 'Destination'}</Label>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setCoinsTarget('personal')} className={`p-3 border transition-all text-sm ${coinsTarget === 'personal' ? 'bg-[#00F0FF]/20 border-[#00F0FF] text-white' : 'bg-[#050508] border-white/10 text-slate-400 hover:border-white/30'}`}>
                <User size={18} className="mx-auto mb-1" />{isItalian ? 'Personale' : 'Personal'}
              </button>
              <button onClick={() => setCoinsTarget('club')} disabled={!clubs.find(c => c.captain_id === selectedUser.user_id)} className={`p-3 border transition-all text-sm ${coinsTarget === 'club' ? 'bg-[#00E676]/20 border-[#00E676] text-white' : 'bg-[#050508] border-white/10 text-slate-400 hover:border-white/30'} ${!clubs.find(c => c.captain_id === selectedUser.user_id) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <Shield size={18} className="mx-auto mb-1" />Club
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Button variant={coinsAction === 'add' ? 'default' : 'outline'} className={coinsAction === 'add' ? 'bg-[#00E676] text-black' : 'border-white/10'} onClick={() => setCoinsAction('add')}>+ {isItalian ? 'Aggiungi' : 'Add'}</Button>
            <Button variant={coinsAction === 'remove' ? 'default' : 'outline'} className={coinsAction === 'remove' ? 'bg-[#FF1744]' : 'border-white/10'} onClick={() => setCoinsAction('remove')}>- {isItalian ? 'Rimuovi' : 'Remove'}</Button>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Quantità' : 'Amount'}</Label>
            <Input type="number" min={0} value={coinsAmount} onChange={(e) => setCoinsAmount(parseInt(e.target.value) || 0)} className="bg-[#050508] border-white/10 text-white font-mono" />
          </div>
          <Button className="w-full btn-primary" onClick={handleModifyCoins} disabled={coinsAmount <= 0 || (coinsTarget === 'club' && !clubs.find(c => c.captain_id === selectedUser.user_id))}>{isItalian ? 'Conferma' : 'Confirm'}</Button>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

// ---- Confirm Delete Dialog (generic) ----
const ConfirmDeleteDialog = ({ isItalian, open, onOpenChange, title, children, onConfirm, deleting, onCancel }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
      <DialogHeader><DialogTitle className="font-heading text-xl uppercase tracking-wider text-[#FF1744]">{title}</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        {children}
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 border-white/10" onClick={onCancel} disabled={deleting}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
          <Button className="flex-1 bg-[#FF1744] hover:bg-[#FF1744]/80" onClick={onConfirm} disabled={deleting} data-testid="confirm-delete-btn">
            {deleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Trash2 size={16} className="mr-2" />{isItalian ? 'Elimina' : 'Delete'}</>}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ---- Delete Club ----
export const DeleteClubDialog = ({ isItalian, open, onOpenChange, clubToDelete, confirmDeleteClub, deleting }) => (
  <ConfirmDeleteDialog isItalian={isItalian} open={open} onOpenChange={onOpenChange} title={isItalian ? 'Elimina Club' : 'Delete Club'} onConfirm={confirmDeleteClub} deleting={deleting} onCancel={() => onOpenChange(false)}>
    {clubToDelete && (
      <div className="p-4 bg-[#FF1744]/10 border border-[#FF1744]/30">
        <p className="text-white text-center">{isItalian ? `Sei sicuro di voler eliminare il club "${clubToDelete.name}"?` : `Are you sure you want to delete club "${clubToDelete.name}"?`}</p>
        <p className="text-sm text-slate-400 text-center mt-2">{isItalian ? 'Questa azione è irreversibile. Tutti i giocatori verranno rimossi dal club.' : 'This action cannot be undone. All players will be removed from the club.'}</p>
      </div>
    )}
  </ConfirmDeleteDialog>
);

// ---- Delete Player ----
export const DeletePlayerDialog = ({ isItalian, open, onOpenChange, playerToDelete, confirmDeletePlayer, deleting }) => (
  <ConfirmDeleteDialog isItalian={isItalian} open={open} onOpenChange={onOpenChange} title={isItalian ? 'Elimina Player' : 'Delete Player'} onConfirm={confirmDeletePlayer} deleting={deleting} onCancel={() => onOpenChange(false)}>
    {playerToDelete && (
      <div className="p-4 bg-[#FF1744]/10 border border-[#FF1744]/30">
        <p className="text-white text-center">{isItalian ? `Sei sicuro di voler eliminare il player "${playerToDelete.name}"?` : `Are you sure you want to delete player "${playerToDelete.name}"?`}</p>
        <p className="text-sm text-slate-400 text-center mt-2">{isItalian ? 'Questa azione è irreversibile.' : 'This action cannot be undone.'}</p>
      </div>
    )}
  </ConfirmDeleteDialog>
);

// ---- Delete User ----
export const DeleteUserDialog = ({ isItalian, open, onOpenChange, userToDelete, confirmDeleteUser, deleting }) => (
  <ConfirmDeleteDialog isItalian={isItalian} open={open} onOpenChange={onOpenChange} title={isItalian ? 'Elimina Utente' : 'Delete User'} onConfirm={confirmDeleteUser} deleting={deleting} onCancel={() => onOpenChange(false)}>
    {userToDelete && (
      <div className="p-4 bg-[#FF1744]/10 border border-[#FF1744]/30">
        <div className="text-center mb-3">
          <p className="font-medium text-white">{userToDelete.name}</p>
          {userToDelete.gamertag && <p className="text-sm text-[#00F0FF]">{userToDelete.gamertag}</p>}
          <p className="text-xs text-slate-400">{userToDelete.email}</p>
        </div>
        <p className="text-white text-center">{isItalian ? 'Sei sicuro di voler eliminare questo utente?' : 'Are you sure you want to delete this user?'}</p>
        <p className="text-sm text-slate-400 text-center mt-2">{isItalian ? 'Questa azione è irreversibile. Tutti i dati associati verranno eliminati.' : 'This action cannot be undone. All associated data will be deleted.'}</p>
      </div>
    )}
  </ConfirmDeleteDialog>
);

// ---- Bulk Delete Users ----
export const BulkDeleteDialog = ({ isItalian, open, onOpenChange, selectedUserIds, users, confirmBulkDeleteUsers, bulkDeleting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
      <DialogHeader><DialogTitle className="font-heading text-xl uppercase tracking-wider text-[#FF1744]">{isItalian ? 'Elimina Utenti Selezionati' : 'Delete Selected Users'}</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div className="p-4 bg-[#FF1744]/10 border border-[#FF1744]/30">
          <p className="text-white text-center text-lg font-bold mb-3">{selectedUserIds.size} {isItalian ? 'utenti selezionati' : 'users selected'}</p>
          <div className="max-h-40 overflow-y-auto space-y-1 mb-3">
            {users.filter(u => selectedUserIds.has(u.user_id)).map(u => (
              <div key={u.user_id} className="flex items-center justify-between px-2 py-1 bg-white/5 rounded text-sm">
                <span className="text-white">{u.gamertag || u.name}</span>
                <span className="text-slate-400 text-xs">{u.role}</span>
              </div>
            ))}
          </div>
          <p className="text-white text-center">{isItalian ? 'Sei sicuro di voler eliminare questi utenti?' : 'Are you sure you want to delete these users?'}</p>
          <p className="text-sm text-slate-400 text-center mt-2">{isItalian ? 'Questa azione è irreversibile. Tutti i dati associati verranno eliminati.' : 'This action cannot be undone. All associated data will be deleted.'}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 border-white/10" onClick={() => onOpenChange(false)} disabled={bulkDeleting}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
          <Button className="flex-1 bg-[#FF1744] hover:bg-[#FF1744]/80" onClick={confirmBulkDeleteUsers} disabled={bulkDeleting} data-testid="confirm-bulk-delete-btn">
            {bulkDeleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Trash2 size={16} className="mr-2" />{isItalian ? `Elimina ${selectedUserIds.size} utenti` : `Delete ${selectedUserIds.size} users`}</>}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ---- Create Championship ----
export const CreateChampionshipDialog = ({ isItalian, open, onOpenChange, championshipForm, setChampionshipForm, creatingChampionship, handleCreateChampionship }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-md max-h-[90vh] p-0 flex flex-col">
      <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/5 shrink-0">
        <DialogTitle className="font-heading text-base uppercase tracking-wider flex items-center gap-2">
          <Trophy size={16} className="text-[#FFEA00]" />{isItalian ? 'Nuovo Campionato' : 'New Championship'}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 px-6 py-4 overflow-y-auto flex-1 min-h-0">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-slate-400 text-[10px] uppercase tracking-wider font-bold">{isItalian ? 'Nome' : 'Name'} *</Label>
            <Input value={championshipForm.name} onChange={(e) => setChampionshipForm(prev => ({ ...prev, name: e.target.value }))} className="bg-[#050508] border-white/10 text-white h-8 text-sm" placeholder="Serie A EPT" data-testid="championship-name-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-400 text-[10px] uppercase tracking-wider font-bold">{isItalian ? 'Stagione' : 'Season'}</Label>
            <Input value={championshipForm.season} onChange={(e) => setChampionshipForm(prev => ({ ...prev, season: e.target.value }))} className="bg-[#050508] border-white/10 text-white h-8 text-sm" placeholder="2024/2025" data-testid="championship-season-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-slate-400 text-[10px] uppercase tracking-wider font-bold">{isItalian ? 'Max Squadre' : 'Max Teams'}</Label>
            <Input type="number" min={2} max={50} value={championshipForm.max_teams} onChange={(e) => setChampionshipForm(prev => ({ ...prev, max_teams: parseInt(e.target.value) || '' }))} onFocus={(e) => e.target.select()} className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="championship-max-teams-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-400 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"><Coins size={10} className="text-[#FFEA00]" />{isItalian ? 'Iscrizione' : 'Entry Fee'}</Label>
            <Input type="number" min={0} step={1000} value={championshipForm.entry_fee} onChange={(e) => setChampionshipForm(prev => ({ ...prev, entry_fee: parseInt(e.target.value) || '' }))} onFocus={(e) => e.target.select()} className="bg-[#050508] border-white/10 text-white h-8 text-sm" placeholder="0 = Free" data-testid="championship-entry-fee-input" />
          </div>
        </div>
        <div className="p-2.5 bg-[#050508]/50 border border-[#00F0FF]/10 rounded space-y-2">
          <p className="text-[10px] text-[#00F0FF] uppercase tracking-wider font-bold">{isItalian ? 'Formato' : 'Format'}</p>
          <div className="space-y-1">
            <Label className="text-slate-500 text-[10px]">{isItalian ? 'Tipo Torneo' : 'Tournament Type'}</Label>
            <Select value={championshipForm.tournament_type || 'groups'} onValueChange={(val) => setChampionshipForm(prev => ({ ...prev, tournament_type: val, num_groups: val === 'knockout' ? 1 : prev.num_groups }))}>
              <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="tournament-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                <SelectItem value="groups">{isItalian ? 'Campionato a Gironi (Round Robin)' : 'Round Robin (Groups)'}</SelectItem>
                <SelectItem value="groups_with_knockout">{isItalian ? 'Gironi + Eliminazione Diretta' : 'Groups + Knockout'}</SelectItem>
                <SelectItem value="knockout">{isItalian ? 'Eliminazione Diretta (Bracket)' : 'Single Elimination (Bracket)'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {championshipForm.tournament_type !== 'knockout' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-slate-500 text-[10px]">{isItalian ? 'Tipo Girone' : 'Round Type'}</Label>
                  <Select value={championshipForm.legs || 'solo_andata'} onValueChange={(val) => setChampionshipForm(prev => ({ ...prev, legs: val }))}>
                    <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="legs-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                      <SelectItem value="solo_andata">{isItalian ? 'Solo Andata (consigliato)' : 'Single Round (recommended)'}</SelectItem>
                      <SelectItem value="andata_ritorno">{isItalian ? 'Andata e Ritorno (2x partite)' : 'Home & Away (2x matches)'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500 text-[10px]">{isItalian ? 'N. Gironi' : 'Groups'}</Label>
                  <Select value={String(championshipForm.num_groups)} onValueChange={(val) => { const ng = parseInt(val); setChampionshipForm(prev => ({ ...prev, num_groups: ng, teams_per_group: ng > 1 ? (prev.teams_per_group || 4) : 0 })); }}>
                    <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="num-groups-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                      {[1, 2, 3, 4, 5, 6, 8].map(n => <SelectItem key={n} value={String(n)}>{n === 1 ? (isItalian ? 'Unico' : 'Single') : n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {championshipForm.num_groups > 1 && (
                  <div className="space-y-1">
                    <Label className="text-slate-500 text-[10px]">{isItalian ? 'Sq./Girone' : 'Teams/Group'}</Label>
                    <Select value={String(championshipForm.teams_per_group || 4)} onValueChange={(val) => setChampionshipForm(prev => ({ ...prev, teams_per_group: parseInt(val) }))}>
                      <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="teams-per-group-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                        {[3, 4, 5, 6, 8, 10].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {championshipForm.num_groups > 1 && championshipForm.teams_per_group > 0 && (
                <p className="text-[10px] text-slate-500">{championshipForm.num_groups} x {championshipForm.teams_per_group} = {championshipForm.num_groups * championshipForm.teams_per_group} {isItalian ? 'squadre' : 'teams'}</p>
              )}
              {(() => {
                // Preview partite girone
                const nPerGroup = championshipForm.num_groups > 1
                  ? (championshipForm.teams_per_group || 0)
                  : (championshipForm.max_teams || 0);
                const groups = championshipForm.num_groups || 1;
                if (nPerGroup < 2) return null;
                const perGroupSingle = (nPerGroup * (nPerGroup - 1)) / 2;
                const multiplier = championshipForm.legs === 'andata_ritorno' ? 2 : 1;
                const totalGroupMatches = perGroupSingle * multiplier * groups;
                return (
                  <div className="mt-1 p-2 bg-[#00F0FF]/5 border border-[#00F0FF]/20 rounded">
                    <p className="text-[10px] text-[#00F0FF] font-bold uppercase tracking-wider mb-0.5">
                      {isItalian ? 'Partite girone previste' : 'Expected group matches'}
                    </p>
                    <p className="text-[10px] text-slate-300">
                      {isItalian
                        ? `${perGroupSingle * multiplier} per girone × ${groups} ${groups === 1 ? 'girone' : 'gironi'} = `
                        : `${perGroupSingle * multiplier} per group × ${groups} ${groups === 1 ? 'group' : 'groups'} = `}
                      <span className="text-white font-bold font-mono">{totalGroupMatches}</span>
                      {' '}{isItalian ? 'partite' : 'matches'}
                    </p>
                    {championshipForm.tournament_type === 'groups_with_knockout' && (
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {isItalian
                          ? `+ fase knockout (${(championshipForm.qualifiers_per_group || 2) * groups} squadre qualificate)`
                          : `+ knockout phase (${(championshipForm.qualifiers_per_group || 2) * groups} qualified teams)`}
                      </p>
                    )}
                  </div>
                );
              })()}
            </>
          )}
          {championshipForm.tournament_type === 'knockout' && (
            <div className="space-y-2 pt-1">
              <div className="space-y-1">
                <Label className="text-slate-500 text-[10px]">{isItalian ? 'Inizia da' : 'Start at'}</Label>
                <Select value={championshipForm.knockout_start_round || 'auto'} onValueChange={(val) => setChampionshipForm(prev => ({ ...prev, knockout_start_round: val }))}>
                  <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="knockout-start-round-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                    <SelectItem value="auto">{isItalian ? 'Automatico (in base alle squadre)' : 'Auto (based on teams)'}</SelectItem>
                    <SelectItem value="trentaduesimi">{isItalian ? 'Trentaduesimi (64 sq.)' : 'Round of 64'}</SelectItem>
                    <SelectItem value="sedicesimi">{isItalian ? 'Sedicesimi (32 sq.)' : 'Round of 32'}</SelectItem>
                    <SelectItem value="ottavi">{isItalian ? 'Ottavi (16 sq.)' : 'Round of 16'}</SelectItem>
                    <SelectItem value="quarti">{isItalian ? 'Quarti (8 sq.)' : 'Quarterfinals (8 t.)'}</SelectItem>
                    <SelectItem value="semifinale">{isItalian ? 'Semifinale (4 sq.)' : 'Semifinals (4 t.)'}</SelectItem>
                    <SelectItem value="finale">{isItalian ? 'Finale (2 sq.)' : 'Final (2 t.)'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[10px] text-slate-400 leading-relaxed">
                {isItalian
                  ? 'Il bracket verrà generato all\'avvio del torneo. Se il numero di squadre è inferiore agli slot del turno scelto, alcuni club passeranno al turno successivo (BYE).'
                  : 'Bracket generated when the tournament starts. If team count is less than chosen round slots, some clubs auto-advance (BYE).'}
              </p>
            </div>
          )}
          {championshipForm.tournament_type === 'groups_with_knockout' && (
            <div className="space-y-2 pt-1">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-slate-500 text-[10px]">{isItalian ? 'Qualif. per girone' : 'Qualifiers/Group'}</Label>
                  <Select value={String(championshipForm.qualifiers_per_group || 2)} onValueChange={(val) => setChampionshipForm(prev => ({ ...prev, qualifiers_per_group: parseInt(val) }))}>
                    <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="qualifiers-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                      {[1, 2, 3, 4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500 text-[10px]">{isItalian ? 'Sorteggio' : 'Draw'}</Label>
                  <Select value={championshipForm.seed_method || 'random'} onValueChange={(val) => setChampionshipForm(prev => ({ ...prev, seed_method: val }))}>
                    <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="seed-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                      <SelectItem value="random">{isItalian ? 'Casuale' : 'Random'}</SelectItem>
                      <SelectItem value="seeded">{isItalian ? 'Cross-girone (1°A vs 2°B…)' : 'Cross-group (1A vs 2B…)'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Best-Nth-placed "ripescaggio" — qualifies the best teams from
                  a given position across all groups (e.g. best thirds when 6
                  groups × 2 = 12 qualifiers and you need 16 to fill ottavi). */}
              <div className="p-2 bg-[#050508] border border-[#FF00E5]/15 rounded space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enable-rescue"
                    checked={!!championshipForm.extra_qualifiers_position}
                    onChange={(e) => setChampionshipForm(prev => ({
                      ...prev,
                      extra_qualifiers_position: e.target.checked ? Math.max(3, (prev.qualifiers_per_group || 2) + 1) : null,
                      extra_qualifiers_count: e.target.checked ? (prev.extra_qualifiers_count || 2) : 0,
                    }))}
                    className="accent-[#FF00E5]"
                    data-testid="enable-rescue-toggle"
                  />
                  <Label htmlFor="enable-rescue" className="text-[11px] text-[#FF00E5] uppercase tracking-wider font-bold cursor-pointer">
                    {isItalian ? 'Ripescaggio migliori N-esime' : 'Best Nth-placed rescue'}
                  </Label>
                </div>
                {championshipForm.extra_qualifiers_position && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-[10px]">{isItalian ? 'Posizione ripescata' : 'Rescue position'}</Label>
                      <Select value={String(championshipForm.extra_qualifiers_position || 3)} onValueChange={(val) => setChampionshipForm(prev => ({ ...prev, extra_qualifiers_position: parseInt(val) }))}>
                        <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="rescue-position-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                          {[3, 4, 5, 6, 7, 8]
                            .filter((n) => n > (championshipForm.qualifiers_per_group || 2))
                            .map(n => <SelectItem key={n} value={String(n)}>{isItalian ? `${n}ª classificata` : `${n}th-placed`}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-[10px]">{isItalian ? 'Quante squadre' : 'How many teams'}</Label>
                      <Select value={String(championshipForm.extra_qualifiers_count || 2)} onValueChange={(val) => setChampionshipForm(prev => ({ ...prev, extra_qualifiers_count: parseInt(val) }))}>
                        <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="rescue-count-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-slate-400 leading-relaxed">
                  {(() => {
                    const direct = (championshipForm.qualifiers_per_group || 2) * (championshipForm.num_groups || 1);
                    const rescued = championshipForm.extra_qualifiers_position ? (championshipForm.extra_qualifiers_count || 0) : 0;
                    const total = direct + rescued;
                    if (isItalian) {
                      return rescued > 0
                        ? `Squadre qualificate: ${direct} dirette + ${rescued} ripescate (${championshipForm.extra_qualifiers_position}ª) = ${total} totali. Le ripescate sono ordinate globalmente per Punti → DR → GF.`
                        : `Abilita per qualificare le migliori squadre da una posizione specifica (es. 4 migliori terze) quando i diretti non raggiungono una potenza di 2.`;
                    }
                    return rescued > 0
                      ? `Qualified teams: ${direct} direct + ${rescued} rescued (${championshipForm.extra_qualifiers_position}th) = ${total} total. Rescued ranked globally by Points → GD → GF.`
                      : `Enable to also qualify the best teams from a specific position across groups.`;
                  })()}
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-500 text-[10px]">{isItalian ? 'Inizia da' : 'Start at'}</Label>
                <Select value={championshipForm.knockout_start_round || 'auto'} onValueChange={(val) => setChampionshipForm(prev => ({ ...prev, knockout_start_round: val }))}>
                  <SelectTrigger className="bg-[#050508] border-white/10 text-white h-8 text-sm" data-testid="hybrid-start-round-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
                    <SelectItem value="auto">{isItalian ? 'Automatico (in base ai qualificati)' : 'Auto (based on qualifiers)'}</SelectItem>
                    <SelectItem value="trentaduesimi">{isItalian ? 'Trentaduesimi' : 'Round of 64'}</SelectItem>
                    <SelectItem value="sedicesimi">{isItalian ? 'Sedicesimi' : 'Round of 32'}</SelectItem>
                    <SelectItem value="ottavi">{isItalian ? 'Ottavi' : 'Round of 16'}</SelectItem>
                    <SelectItem value="quarti">{isItalian ? 'Quarti' : 'Quarterfinals'}</SelectItem>
                    <SelectItem value="semifinale">{isItalian ? 'Semifinale' : 'Semifinals'}</SelectItem>
                    <SelectItem value="finale">{isItalian ? 'Finale' : 'Final'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[10px] text-slate-400 leading-relaxed">
                {isItalian
                  ? `Dopo la fase a gironi, le prime ${championshipForm.qualifiers_per_group || 2} di ogni girone (totale ${(championshipForm.qualifiers_per_group || 2) * (championshipForm.num_groups || 1)} squadre) accederanno alla fase a eliminazione diretta. Il bracket verrà generato manualmente dall'admin.`
                  : `After the group phase, the top ${championshipForm.qualifiers_per_group || 2} of each group (total ${(championshipForm.qualifiers_per_group || 2) * (championshipForm.num_groups || 1)}) advance to the knockout. Bracket generated manually by admin.`}
              </p>
            </div>
          )}
        </div>
        <div className="p-2.5 bg-[#050508]/50 border border-[#FF00E5]/15 rounded space-y-2">
          <p className="text-[10px] text-[#FF00E5] uppercase tracking-wider font-bold">{isItalian ? 'Orario partite' : 'Match kickoff'}</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-slate-500 text-[10px]">{isItalian ? 'Ora di inizio' : 'Start time'}</Label>
              <Input
                type="time"
                value={championshipForm.match_start_time || '21:00'}
                onChange={(e) => setChampionshipForm(prev => ({ ...prev, match_start_time: e.target.value }))}
                className="bg-[#050508] border-white/10 text-white h-8 text-sm"
                data-testid="match-start-time-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-500 text-[10px]">{isItalian ? 'Tolleranza (min)' : 'Tolerance (min)'}</Label>
              <Input
                type="number"
                min={0}
                max={60}
                step={1}
                value={championshipForm.tolerance_minutes ?? 15}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const v = e.target.value === '' ? '' : Math.max(0, Math.min(60, parseInt(e.target.value) || 0));
                  setChampionshipForm(prev => ({ ...prev, tolerance_minutes: v }));
                }}
                className="bg-[#050508] border-white/10 text-white h-8 text-sm"
                data-testid="tolerance-minutes-input"
              />
            </div>
          </div>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            {isItalian
              ? `Ogni partita sarà programmata alle ${championshipForm.match_start_time || '21:00'}. Dopo ${championshipForm.tolerance_minutes ?? 15} min di ritardo, la squadra assente può essere segnalata come forfeit.`
              : `Matches kick off at ${championshipForm.match_start_time || '21:00'}. After ${championshipForm.tolerance_minutes ?? 15} min late, the missing team may be reported as forfeit.`}
          </p>
        </div>
        <div className="flex items-center justify-between p-2 bg-[#050508] border border-white/5 rounded text-sm">
          <span className="text-white font-medium truncate">{championshipForm.name || '---'}</span>
          {championshipForm.entry_fee > 0 ? (
            <span className="px-2 py-0.5 bg-[#FFEA00]/20 text-[#FFEA00] rounded text-xs font-mono ml-2 shrink-0">{championshipForm.entry_fee.toLocaleString()}</span>
          ) : (
            <span className="px-2 py-0.5 bg-[#00E676]/20 text-[#00E676] rounded text-xs font-bold ml-2 shrink-0">FREE</span>
          )}
        </div>
      </div>
      <div className="px-6 py-4 border-t border-white/5 shrink-0 bg-[#0a0d15]">
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 border-white/10" onClick={() => { onOpenChange(false); setChampionshipForm({ name: '', season: '2024/2025', max_teams: 20, entry_fee: 0, num_groups: 1, teams_per_group: 0, legs: 'solo_andata', tournament_type: 'groups', qualifiers_per_group: 2, extra_qualifiers_position: null, extra_qualifiers_count: 0, seed_method: 'random', knockout_start_round: 'auto' }); }} disabled={creatingChampionship}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
          <Button className="flex-1 btn-primary" onClick={handleCreateChampionship} disabled={creatingChampionship || !championshipForm.name.trim()} data-testid="confirm-create-championship-btn">
            {creatingChampionship ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Plus size={16} className="mr-2" />{isItalian ? 'Crea Campionato' : 'Create Championship'}</>}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ---- Delete Championship ----
export const DeleteChampionshipDialog = ({ isItalian, open, onOpenChange, championshipToDelete, confirmDeleteChampionship, deleting }) => (
  <ConfirmDeleteDialog isItalian={isItalian} open={open} onOpenChange={onOpenChange} title={isItalian ? 'Elimina Campionato' : 'Delete Championship'} onConfirm={confirmDeleteChampionship} deleting={deleting} onCancel={() => onOpenChange(false)}>
    {championshipToDelete && (
      <div className="p-4 bg-[#FF1744]/10 border border-[#FF1744]/30">
        <div className="flex items-center gap-3 justify-center mb-3">
          <Trophy size={24} className="text-[#FFEA00]" />
          <div className="text-center"><p className="font-medium text-white">{championshipToDelete.name}</p><p className="text-sm text-slate-400">{championshipToDelete.season}</p></div>
        </div>
        <p className="text-white text-center">{isItalian ? 'Sei sicuro di voler eliminare questo campionato?' : 'Are you sure you want to delete this championship?'}</p>
        <p className="text-sm text-slate-400 text-center mt-2">{isItalian ? 'Questa azione è irreversibile. Tutte le partite associate verranno eliminate.' : 'This action cannot be undone. All associated matches will be deleted.'}</p>
      </div>
    )}
  </ConfirmDeleteDialog>
);

// ---- Ban User ----
export const BanUserDialog = ({ isItalian, open, onOpenChange, userToBan, confirmBanUser }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2">
          <Ban size={20} className={userToBan?.is_banned ? 'text-[#00E676]' : 'text-[#FF1744]'} />
          {userToBan?.is_banned ? (isItalian ? 'Sblocca Utente' : 'Unban User') : (isItalian ? 'Banna Utente' : 'Ban User')}
        </DialogTitle>
      </DialogHeader>
      {userToBan && (
        <div className="space-y-4 mt-4">
          <div className="p-4 bg-[#050508] border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#00F0FF]/20 flex items-center justify-center"><User size={24} className="text-[#00F0FF]" /></div>
              <div>
                <p className="font-bold text-white">{userToBan.gamertag || userToBan.name}</p>
                <p className="text-sm text-slate-400">{userToBan.email}</p>
                <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded uppercase ${userToBan.role === 'super_admin' ? 'bg-[#FFEA00]/20 text-[#FFEA00]' : userToBan.role === 'admin' ? 'bg-[#D500F9]/20 text-[#D500F9]' : 'bg-slate-500/20 text-slate-400'}`}>{userToBan.role}</span>
              </div>
            </div>
          </div>
          <p className="text-center text-slate-300">{userToBan.is_banned ? (isItalian ? 'Vuoi sbloccare questo utente?' : 'Do you want to unban this user?') : (isItalian ? 'Vuoi bannare questo utente?' : 'Do you want to ban this user?')}</p>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 border-white/10" onClick={() => onOpenChange(false)}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
            <Button className={`flex-1 ${userToBan.is_banned ? 'bg-[#00E676] hover:bg-[#00E676]/90 text-black' : 'bg-[#FF1744] hover:bg-[#FF1744]/90'}`} onClick={confirmBanUser}>
              <Ban size={16} className="mr-2" />{userToBan.is_banned ? (isItalian ? 'Sblocca' : 'Unban') : (isItalian ? 'Banna' : 'Ban')}
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

// ---- Self-Ban ----
export const SelfBanDialog = ({ isItalian, open, onOpenChange, confirmSelfBan }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-[#FF1744]/50 text-white">
      <DialogHeader><DialogTitle className="font-heading text-xl uppercase tracking-wider text-[#FF1744] flex items-center gap-2"><Ban size={24} />{isItalian ? 'ATTENZIONE!' : 'WARNING!'}</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div className="p-4 bg-[#FF1744]/20 border border-[#FF1744]/50">
          <p className="text-white font-bold text-center mb-2">{isItalian ? 'Stai per bannarti!' : 'You are about to ban yourself!'}</p>
          <p className="text-sm text-slate-300 text-center">{isItalian ? 'Questa azione ti disconnetterà immediatamente e non potrai più accedere al sistema. Solo un altro Super Admin potrà sbloccarti.' : 'This action will log you out immediately and you will no longer be able to access the system. Only another Super Admin can unban you.'}</p>
        </div>
        <div className="p-3 bg-[#FFEA00]/10 border border-[#FFEA00]/30"><p className="text-xs text-[#FFEA00] text-center">{isItalian ? 'Se sei l\'unico Super Admin, perderai l\'accesso per sempre!' : 'If you are the only Super Admin, you will lose access forever!'}</p></div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 border-white/10" onClick={() => onOpenChange(false)}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
          <Button className="flex-1 bg-[#FF1744] hover:bg-[#FF1744]/80" onClick={confirmSelfBan}><Ban size={16} className="mr-2" />{isItalian ? 'Confermo, Bannami' : 'Confirm, Ban Me'}</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ---- Edit Club ----
export const EditClubDialog = ({ isItalian, open, onOpenChange, editingClub, editClubForm, setEditClubForm, savingClub, handleSaveClub, handleUploadClubLogo, editClubLogoRef }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
      <DialogHeader><DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2"><Edit size={20} className="text-[#00F0FF]" />{isItalian ? 'Modifica Club' : 'Edit Club'}</DialogTitle></DialogHeader>
      {editingClub && (
        <div className="space-y-6 mt-4">
          <div className="flex flex-col items-center gap-4">
            <div className="relative group">
              <div className="w-24 h-24 rounded-lg bg-[#050508] border-2 border-white/20 flex items-center justify-center overflow-hidden">
                {editingClub.logo ? <img src={editingClub.logo} alt={editingClub.name} className="w-full h-full object-cover" /> : <Shield size={36} className="text-[#00F0FF]" />}
              </div>
              <div className="absolute inset-0 rounded-lg bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={() => editClubLogoRef.current?.click()}>
                <Camera size={24} className="text-white" />
              </div>
            </div>
            <input ref={editClubLogoRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleUploadClubLogo} className="hidden" />
            <Button variant="outline" size="sm" className="border-white/10" onClick={() => editClubLogoRef.current?.click()} disabled={savingClub}><Upload size={14} className="mr-2" />{isItalian ? 'Carica Logo' : 'Upload Logo'}</Button>
            <p className="text-xs text-slate-500">JPG, PNG, GIF, WEBP - Max 2MB</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Nome Club' : 'Club Name'}</Label>
            <Input value={editClubForm.name} onChange={(e) => setEditClubForm({ ...editClubForm, name: e.target.value })} placeholder={isItalian ? 'Nome del club' : 'Club name'} className="bg-[#050508] border-white/10 text-white" />
          </div>
          <div className="p-3 bg-[#050508] border border-white/10 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-500">{isItalian ? 'Capitano' : 'Captain'}:</span><span className="text-white">{editingClub.captain_name || '-'}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">{isItalian ? 'Giocatori' : 'Players'}:</span><span className="text-white">{editingClub.players?.length || 0}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Coins:</span><span className="text-[#FFEA00] font-mono">{editingClub.coins?.toLocaleString()}</span></div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 border-white/10" onClick={() => onOpenChange(false)}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
            <Button className="flex-1 bg-[#00F0FF] hover:bg-[#00F0FF]/90" onClick={handleSaveClub} disabled={savingClub || !editClubForm.name.trim()}>
              {savingClub ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : null}{isItalian ? 'Salva' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

// ---- Send Coins ----
export const SendCoinsDialog = ({ isItalian, open, onOpenChange, coinsRecipient, coinsForm, setCoinsForm, sendingCoins, handleSendCoins }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
      <DialogHeader><DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2"><Coins size={20} className="text-[#FFEA00]" />{isItalian ? 'Invia Coins' : 'Send Coins'}</DialogTitle></DialogHeader>
      {coinsRecipient && (
        <div className="space-y-4 mt-4">
          <div className="p-3 bg-[#050508] border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#00F0FF]/20 flex items-center justify-center"><User size={20} className="text-[#00F0FF]" /></div>
              <div><p className="font-bold text-white">{coinsRecipient.gamertag || coinsRecipient.name}</p><p className="text-xs text-slate-400">{isItalian ? 'Coins personali' : 'Personal coins'}: {(coinsRecipient.personal_coins || 0).toLocaleString()}</p></div>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Destinazione' : 'Destination'}</Label>
            <div className="flex gap-2">
              <Button type="button" variant={coinsForm.target === 'personal' ? 'default' : 'outline'} className={coinsForm.target === 'personal' ? 'flex-1 bg-[#FFEA00] text-black hover:bg-[#FFEA00]/90' : 'flex-1 border-white/10'} onClick={() => setCoinsForm({ ...coinsForm, target: 'personal' })}><User size={14} className="mr-2" />{isItalian ? 'Personali' : 'Personal'}</Button>
              <Button type="button" variant={coinsForm.target === 'club' ? 'default' : 'outline'} className={coinsForm.target === 'club' ? 'flex-1 bg-[#00F0FF] text-white hover:bg-[#00F0FF]/90' : 'flex-1 border-white/10'} onClick={() => setCoinsForm({ ...coinsForm, target: 'club' })} disabled={coinsRecipient.role !== 'captain'}><Shield size={14} className="mr-2" />Club</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Importo' : 'Amount'}</Label>
            <Input type="number" value={coinsForm.amount} onChange={(e) => setCoinsForm({ ...coinsForm, amount: e.target.value })} onFocus={(e) => e.target.select()} placeholder={isItalian ? 'Usa numeri negativi per rimuovere' : 'Use negative numbers to remove'} className="bg-[#050508] border-white/10 text-white" />
            <p className="text-xs text-slate-500">{isItalian ? 'Inserisci un valore negativo per rimuovere coins' : 'Enter a negative value to remove coins'}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Motivo' : 'Reason'}</Label>
            <Input value={coinsForm.reason} onChange={(e) => setCoinsForm({ ...coinsForm, reason: e.target.value })} placeholder={isItalian ? 'Es: Premio torneo, Bonus...' : 'E.g.: Tournament prize, Bonus...'} className="bg-[#050508] border-white/10 text-white" />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 border-white/10" onClick={() => onOpenChange(false)}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
            <Button className="flex-1 bg-[#FFEA00] hover:bg-[#FFEA00]/90 text-black font-bold" onClick={handleSendCoins} disabled={sendingCoins || !coinsForm.amount || !coinsForm.reason}>
              {sendingCoins ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" /> : <Coins size={14} className="mr-2" />}{isItalian ? 'Invia' : 'Send'}
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

// ---- Roster Dialog ----
export const RosterDialog = ({ isItalian, open, onOpenChange, selectedClubRoster, clubMembers, loadingRoster, handleAssignRole }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-2xl">
      <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><Shield className="text-[#00F0FF]" />{isItalian ? 'Roster' : 'Roster'}: {selectedClubRoster?.name}</DialogTitle></DialogHeader>
      {loadingRoster ? (
        <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div></div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-slate-400">{isItalian ? 'Giocatore' : 'Player'}</TableHead>
                <TableHead className="text-slate-400">{isItalian ? 'Ruolo' : 'Role'}</TableHead>
                <TableHead className="text-slate-400">{isItalian ? 'Stato' : 'Status'}</TableHead>
                <TableHead className="text-slate-400 text-right">{isItalian ? 'Azioni' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clubMembers.map((member) => (
                <TableRow key={member.user_id} className="border-white/10">
                  <TableCell><div><p className="font-medium text-white">{member.gamertag || member.name}</p><p className="text-xs text-slate-500">{member.email}</p></div></TableCell>
                  <TableCell>{member.player_role ? <span className="px-2 py-0.5 text-xs rounded bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30 font-semibold">{member.player_role}</span> : <span className="text-slate-500">-</span>}</TableCell>
                  <TableCell>
                    {member.is_captain ? <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 font-semibold">{isItalian ? 'Capitano' : 'Captain'}</span>
                     : member.is_vice_captain ? <span className="px-2 py-0.5 text-xs rounded bg-[#AA00FF]/20 text-[#AA00FF] border border-[#AA00FF]/30 font-semibold">{isItalian ? 'Vice Capitano' : 'Vice Captain'}</span> : null}
                  </TableCell>
                  <TableCell className="text-right">
                    {!member.is_captain && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-white" data-testid={`assign-role-${member.user_id}`}><Crown size={14} className="mr-1" /><ChevronDown size={12} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#0a0d15] border-white/10">
                          {member.is_vice_captain && <DropdownMenuItem onClick={() => handleAssignRole(member.user_id, 'captain')} className="text-yellow-400 focus:text-yellow-400 focus:bg-yellow-500/10 cursor-pointer" data-testid={`make-captain-${member.user_id}`}><Crown size={14} className="mr-2" />{isItalian ? 'Promuovi a Capitano' : 'Promote to Captain'}</DropdownMenuItem>}
                          {!member.is_vice_captain && <DropdownMenuItem onClick={() => handleAssignRole(member.user_id, 'vice_captain')} className="text-[#AA00FF] focus:text-[#AA00FF] focus:bg-[#AA00FF]/10 cursor-pointer" data-testid={`make-vice-captain-${member.user_id}`}><Shield size={14} className="mr-2" />{isItalian ? 'Rendi Vice Capitano' : 'Make Vice Captain'}</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {clubMembers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">{isItalian ? 'Nessun giocatore nel roster' : 'No players in roster'}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <div className="flex justify-between items-center pt-4 border-t border-white/10">
        <p className="text-sm text-slate-400">{isItalian ? 'Totale giocatori' : 'Total players'}: {clubMembers.length}</p>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/20 hover:bg-white/5">{isItalian ? 'Chiudi' : 'Close'}</Button>
      </div>
    </DialogContent>
  </Dialog>
);

// ---- Create Club ----
export const CreateClubDialog = ({ isItalian, open, onOpenChange, createClubForm, setCreateClubForm, creatingClub, availableCaptains, championships, handleCreateClub }) => {
  const [captainSearch, setCaptainSearch] = React.useState('');
  const [showCaptainList, setShowCaptainList] = React.useState(false);

  // Reset search when dialog closes
  React.useEffect(() => {
    if (!open) {
      setCaptainSearch('');
      setShowCaptainList(false);
    }
  }, [open]);

  const selectedCaptain = availableCaptains.find(u => u.user_id === createClubForm.captainId);
  const normalizedSearch = captainSearch.trim().toLowerCase();
  const filteredCaptains = normalizedSearch
    ? availableCaptains.filter(u => {
        const gt = (u.gamertag || '').toLowerCase();
        const nm = (u.name || '').toLowerCase();
        const em = (u.email || '').toLowerCase();
        return gt.includes(normalizedSearch) || nm.includes(normalizedSearch) || em.includes(normalizedSearch);
      })
    : availableCaptains;

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
      <DialogHeader><DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2"><Shield size={20} className="text-[#00F0FF]" />{isItalian ? 'Crea Nuovo Club' : 'Create New Club'}</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Nome Club' : 'Club Name'} *</Label>
          <Input value={createClubForm.name} onChange={(e) => setCreateClubForm(prev => ({ ...prev, name: e.target.value }))} className="bg-[#050508] border-white/10 text-white" placeholder={isItalian ? 'Inserisci il nome del club' : 'Enter club name'} data-testid="create-club-name-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Capitano' : 'Captain'} *</Label>

          {/* Searchable captain combobox */}
          {selectedCaptain && !showCaptainList ? (
            <div className="flex items-center justify-between gap-2 p-2 bg-[#050508] border border-[#00F0FF]/40 rounded">
              <div className="flex items-center gap-2 min-w-0">
                <Check size={16} className="text-[#00F0FF] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">{selectedCaptain.gamertag || selectedCaptain.name}</div>
                  <div className="text-xs text-slate-400 truncate">{selectedCaptain.email}</div>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setShowCaptainList(true); setCaptainSearch(''); }}
                className="text-slate-400 hover:text-white shrink-0"
                data-testid="change-selected-captain-btn"
              >
                <X size={14} />
              </Button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                <Input
                  type="text"
                  value={captainSearch}
                  onChange={(e) => { setCaptainSearch(e.target.value); setShowCaptainList(true); }}
                  onFocus={() => setShowCaptainList(true)}
                  placeholder={isItalian ? 'Cerca per gamertag, nome o email...' : 'Search by gamertag, name or email...'}
                  className="pl-10 bg-[#050508] border-white/10 text-white placeholder:text-slate-500"
                  data-testid="captain-search-input"
                />
              </div>
              <div className="border border-white/10 rounded bg-[#050508] max-h-56 overflow-y-auto" data-testid="captain-list">
                {filteredCaptains.length === 0 ? (
                  <div className="p-3 text-xs text-slate-500 text-center">
                    {availableCaptains.length === 0
                      ? (isItalian ? 'Nessun utente disponibile senza club' : 'No available users without a club')
                      : (isItalian ? 'Nessun utente trovato' : 'No user found')}
                  </div>
                ) : (
                  filteredCaptains.slice(0, 50).map(u => {
                    const isSelected = u.user_id === createClubForm.captainId;
                    return (
                      <button
                        key={u.user_id}
                        type="button"
                        onClick={() => {
                          setCreateClubForm(prev => ({ ...prev, captainId: u.user_id }));
                          setShowCaptainList(false);
                          setCaptainSearch('');
                        }}
                        className={`w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-[#00F0FF]/10 border-b border-white/5 last:border-0 transition-colors ${
                          isSelected ? 'bg-[#00F0FF]/15' : ''
                        }`}
                        data-testid={`captain-option-${u.user_id}`}
                      >
                        <User size={14} className="text-slate-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white truncate">{u.gamertag || u.name}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email}</div>
                        </div>
                        {isSelected && <Check size={14} className="text-[#00F0FF] shrink-0" />}
                      </button>
                    );
                  })
                )}
                {filteredCaptains.length > 50 && (
                  <div className="px-3 py-2 text-xs text-slate-500 text-center border-t border-white/5">
                    {isItalian
                      ? `${filteredCaptains.length - 50} altri utenti — affina la ricerca`
                      : `${filteredCaptains.length - 50} more users — refine search`}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {isItalian
                  ? `${availableCaptains.length} utenti disponibili senza club`
                  : `${availableCaptains.length} available users without a club`}
              </p>
            </>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Campionato (opzionale)' : 'Championship (optional)'}</Label>
          <select value={createClubForm.championshipId} onChange={(e) => setCreateClubForm(prev => ({ ...prev, championshipId: e.target.value }))} className="w-full bg-[#050508] border border-white/10 text-white px-3 py-2 text-sm" data-testid="create-club-championship-select">
            <option value="">{isItalian ? '-- Nessun Campionato --' : '-- No Championship --'}</option>
            {championships.filter(c => c.status === 'registration').map(c => <option key={c.championship_id} value={c.championship_id}>{c.name} ({c.clubs?.length || 0}/{c.max_teams})</option>)}
          </select>
          <p className="text-xs text-slate-500">{isItalian ? 'Se selezionato, il club verrà aggiunto automaticamente al campionato' : 'If selected, the club will be automatically added to the championship'}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 border-white/10" onClick={() => onOpenChange(false)} disabled={creatingClub}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
          <Button className="flex-1 btn-primary" onClick={handleCreateClub} disabled={creatingClub || !createClubForm.name.trim() || !createClubForm.captainId} data-testid="confirm-create-club-btn">
            {creatingClub ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Plus size={16} className="mr-2" />{isItalian ? 'Crea Club' : 'Create Club'}</>}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};

// ---- Change Password ----
export const ChangePasswordDialog = ({ isItalian, open, onOpenChange, passwordTarget, newPassword, setNewPassword, changingPassword, confirmChangePassword }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-md">
      <DialogHeader><DialogTitle className="flex items-center gap-2 text-white"><Lock size={20} className="text-[#FF9100]" />{isItalian ? 'Cambia Password' : 'Change Password'}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div className="p-3 bg-[#050508] border border-white/10">
          <p className="text-xs text-slate-500 uppercase tracking-wider">{isItalian ? 'Utente' : 'User'}</p>
          <p className="text-white font-semibold">{passwordTarget?.gamertag || passwordTarget?.name || passwordTarget?.email}</p>
          <p className="text-xs text-slate-400">{passwordTarget?.email}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Nuova Password' : 'New Password'}</Label>
          <Input data-testid="admin-new-password-input" type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={isItalian ? 'Inserisci nuova password...' : 'Enter new password...'} className="bg-[#050508] border-white/10 text-white" />
          <p className="text-xs text-slate-500">{isItalian ? 'Minimo 4 caratteri' : 'Minimum 4 characters'}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-white/10 text-slate-300 hover:bg-white/5">{isItalian ? 'Annulla' : 'Cancel'}</Button>
          <Button data-testid="confirm-change-password-btn" onClick={confirmChangePassword} disabled={changingPassword || newPassword.length < 4} className="flex-1 bg-[#FF9100] hover:bg-[#FF9100]/90 text-white disabled:opacity-40">
            {changingPassword ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Lock size={16} className="mr-2" />}{isItalian ? 'Cambia Password' : 'Change Password'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);


// ---- Create User ----
export const CreateUserDialog = ({ isItalian, open, onOpenChange, createUserForm, setCreateUserForm, creatingUser, handleCreateUser }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-md">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2">
          <Plus size={20} className="text-[#00E676]" />{isItalian ? 'Crea Nuovo Utente' : 'Create New User'}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">Email *</Label>
          <Input type="email" value={createUserForm.email} onChange={(e) => setCreateUserForm(prev => ({ ...prev, email: e.target.value }))} placeholder="user@example.com" className="bg-[#050508] border-white/10 text-white" data-testid="create-user-email" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">Gamertag *</Label>
          <Input value={createUserForm.gamertag} onChange={(e) => setCreateUserForm(prev => ({ ...prev, gamertag: e.target.value }))} placeholder="NomeUtente123" className="bg-[#050508] border-white/10 text-white" data-testid="create-user-gamertag" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">Password *</Label>
          <Input type="text" value={createUserForm.password} onChange={(e) => setCreateUserForm(prev => ({ ...prev, password: e.target.value }))} placeholder={isItalian ? 'Minimo 4 caratteri' : 'Min 4 characters'} className="bg-[#050508] border-white/10 text-white" data-testid="create-user-password" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Data di Nascita' : 'Birth Date'}</Label>
            <Input type="date" value={createUserForm.birth_date} onChange={(e) => setCreateUserForm(prev => ({ ...prev, birth_date: e.target.value }))} className="bg-[#050508] border-white/10 text-white" data-testid="create-user-birthdate" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Telefono' : 'Phone'}</Label>
            <Input type="tel" value={createUserForm.phone} onChange={(e) => setCreateUserForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="+39 333 1234567" className="bg-[#050508] border-white/10 text-white" data-testid="create-user-phone" />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Ruolo' : 'Role'} *</Label>
          <Select value={createUserForm.role || 'player'} onValueChange={(val) => setCreateUserForm(prev => ({ ...prev, role: val }))}>
            <SelectTrigger className="bg-[#050508] border-white/10 text-white" data-testid="create-user-role-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
              <SelectItem value="player">{isItalian ? 'Giocatore' : 'Player'}</SelectItem>
              <SelectItem value="captain">{isItalian ? 'Capitano' : 'Captain'}</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Ruolo in Campo' : 'Position'}</Label>
          <Select value={createUserForm.player_role || '_none'} onValueChange={(val) => setCreateUserForm(prev => ({ ...prev, player_role: val === '_none' ? '' : val }))}>
            <SelectTrigger className="bg-[#050508] border-white/10 text-white" data-testid="create-user-position-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0a0d15] border-white/10 text-white">
              <SelectItem value="_none">{isItalian ? '-- Nessuno --' : '-- None --'}</SelectItem>
              <SelectItem value="GK">GK - {isItalian ? 'Portiere' : 'Goalkeeper'}</SelectItem>
              <SelectItem value="DC">DC - {isItalian ? 'Difensore Centrale' : 'Center Back'}</SelectItem>
              <SelectItem value="CDC">CDC - {isItalian ? 'Centrocampista Difensivo' : 'Defensive Mid'}</SelectItem>
              <SelectItem value="ES">ES - {isItalian ? 'Esterno Sinistro' : 'Left Wing'}</SelectItem>
              <SelectItem value="ED">ED - {isItalian ? 'Esterno Destro' : 'Right Wing'}</SelectItem>
              <SelectItem value="COC">COC - {isItalian ? 'Centrocampista Offensivo' : 'Offensive Mid'}</SelectItem>
              <SelectItem value="ATT">ATT - {isItalian ? 'Attaccante' : 'Striker'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-3 pt-2">
          <Button variant="outline" className="flex-1 border-white/10" onClick={() => onOpenChange(false)} disabled={creatingUser}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
          <Button className="flex-1 bg-[#00E676] hover:bg-[#00E676]/90 text-black font-bold" onClick={handleCreateUser} disabled={creatingUser || !createUserForm.email.trim() || !createUserForm.gamertag.trim() || !createUserForm.password.trim()} data-testid="confirm-create-user-btn">
            {creatingUser ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Plus size={16} className="mr-2" />{isItalian ? 'Crea Utente' : 'Create User'}</>}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);


// ---- Add Member to Club ----
export const AddMemberDialog = ({ isItalian, open, onOpenChange, addMemberClub, addMemberUserId, setAddMemberUserId, addingMember, usersWithoutClub, handleAddMemberToClub }) => {
  const [memberSearch, setMemberSearch] = React.useState('');
  const selectedUser = usersWithoutClub.find(u => u.user_id === addMemberUserId);

  const filtered = memberSearch.trim()
    ? usersWithoutClub.filter(u => {
        const q = memberSearch.toLowerCase();
        return (u.gamertag?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q));
      })
    : [];

  return (
  <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setMemberSearch(''); }}>
    <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-md">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-2">
          <User size={20} className="text-[#00F0FF]" />{isItalian ? 'Aggiungi Membro' : 'Add Member'}
        </DialogTitle>
      </DialogHeader>
      {addMemberClub && (
        <div className="space-y-4 mt-4">
          <div className="p-3 bg-[#050508] border border-white/10 flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-[#00F0FF]/20 flex items-center justify-center">
              <Shield size={20} className="text-[#00F0FF]" />
            </div>
            <div>
              <p className="font-bold text-white">{addMemberClub.name}</p>
              <p className="text-xs text-slate-400">{addMemberClub.players?.length || 0} {isItalian ? 'giocatori' : 'players'}</p>
            </div>
          </div>

          {selectedUser && (
            <div className="p-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded flex items-center justify-between">
              <div>
                <p className="text-white font-semibold text-sm">{selectedUser.gamertag || selectedUser.name}</p>
                <p className="text-xs text-slate-400">{selectedUser.email}</p>
              </div>
              <button onClick={() => { setAddMemberUserId(''); setMemberSearch(''); }} className="text-slate-400 hover:text-white">
                <Trash2 size={14} />
              </button>
            </div>
          )}

          {!selectedUser && (
            <div className="space-y-2">
              <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">{isItalian ? 'Cerca per email o gamertag' : 'Search by email or gamertag'}</Label>
              <div className="relative">
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder={isItalian ? 'Scrivi email o gamertag...' : 'Type email or gamertag...'}
                  className="w-full bg-[#050508] border border-white/10 text-white px-3 py-2 text-sm rounded focus:outline-none focus:border-[#00F0FF]/50"
                  data-testid="add-member-search-input"
                  autoFocus
                />
              </div>
              {memberSearch.trim() && filtered.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-white/10 rounded bg-[#050508]">
                  {filtered.slice(0, 20).map(u => (
                    <button
                      key={u.user_id}
                      onClick={() => { setAddMemberUserId(u.user_id); setMemberSearch(''); }}
                      className="w-full text-left px-3 py-2 hover:bg-white/10 transition-colors border-b border-white/5 last:border-b-0"
                      data-testid={`add-member-option-${u.user_id}`}
                    >
                      <p className="text-white text-sm font-medium">{u.gamertag || u.name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </button>
                  ))}
                </div>
              )}
              {memberSearch.trim() && filtered.length === 0 && (
                <p className="text-xs text-orange-400 py-2">{isItalian ? 'Nessun utente trovato senza club' : 'No users found without a club'}</p>
              )}
              {!memberSearch.trim() && (
                <p className="text-xs text-slate-500">{isItalian ? `${usersWithoutClub.length} utenti disponibili` : `${usersWithoutClub.length} available users`}</p>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1 border-white/10" onClick={() => { onOpenChange(false); setMemberSearch(''); }} disabled={addingMember}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
            <Button className="flex-1 bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-bold" onClick={handleAddMemberToClub} disabled={addingMember || !addMemberUserId} data-testid="confirm-add-member-btn">
              {addingMember ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Plus size={16} className="mr-2" />{isItalian ? 'Aggiungi' : 'Add'}</>}
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
  );
};
